import { greaterThanOrEqual, subtract } from "dinero.js";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { Link } from "../../../components/Link";
import { getLowestThresholdPlan } from "../../../utils/financing";
import { formatAPR, formatTermLength, money } from "../../../utils/format";
import { getDinero, getMonthlyPrice } from "../../../utils/money";
import { usePageURL } from "../../../utils/urls";

import styles from "./index.module.scss";

interface IProps {
    grandTotal: string;
}

export const FinancingUpsellBlock = (props: IProps) => {
    const grandTotal = getDinero(props.grandTotal);
    const lowestPlan = getLowestThresholdPlan();
    const homeURL = usePageURL("home");
    if (!lowestPlan) {
        return null;
    }
    // Already meet plan threshold?
    if (greaterThanOrEqual(grandTotal, lowestPlan.threshold)) {
        return null;
    }
    const orderTotalDelta = subtract(lowestPlan.threshold, grandTotal);
    const costPerMonth = getMonthlyPrice(
        lowestPlan.threshold,
        lowestPlan.length,
    );
    return (
        <div className={styles.root}>
            <p className={styles.headline}>
                {interpolate(
                    t`Special financing on orders of %(threshold)s or more!`,
                    { threshold: money(lowestPlan.threshold) },
                )}
            </p>
            <p className={styles.marginBottom}>
                {/* TODO(i18n): Hardcoded punctuation outside translation. Move period and superscript inside t string to allow proper localization of sentence structure */}
                {interpolate(
                    t`Add products totaling %(threshold)s or more to qualify for %(apr)s APR for %(length)s months`,
                    {
                        threshold: money(orderTotalDelta),
                        apr: formatAPR(lowestPlan.apr),
                        length: formatTermLength(lowestPlan.length),
                    },
                )}
                <sup>1</sup>.
            </p>
            <div className={styles.cta}>
                <div className={styles.ctaTitle}>
                    {t`Monthly Payments starting at:`}
                </div>
                <div className={styles.ctaCopy}>
                    {/* TODO(i18n): Hardcoded abbreviation "/mo." should be translatable. Different languages may use different abbreviations for "per month" */}
                    {richT(
                        interpolate(
                            t`%(costPerMonth)s/mo.<sup>2</sup> for %(length)s months<sup>%(superscript)s</sup> `,
                            {
                                costPerMonth: `${money(costPerMonth)}`,
                                length: `${lowestPlan.length}`,
                                superscript: `${lowestPlan.superscript}`,
                            },
                        ),
                    )}
                    {interpolate(t`(total payment of %(threshold)s)`, {
                        threshold: money(lowestPlan.threshold),
                    })}
                </div>
            </div>
            <p className={styles.link}>
                <Link href={homeURL}>{t`Continue Shopping`}</Link>
            </p>
        </div>
    );
};
